import { AnalystOutputBlock, AnalystOutputType, Citation } from 'src/app/models/analyst-output.model';

const MAX_VISIBLE_CITATION_MARKERS = 3;
const ROUTE_RESOLUTION_BASE = 'https://app.invalid';

export interface CitationMarker {
  index: number;
  citation: Citation;
}

export interface CappedCitationMarkers {
  visible: CitationMarker[];
  hidden: CitationMarker[];
}

export interface CitationNumbering {
  readonly sources: readonly Citation[];
  readonly numberById: ReadonlyMap<string, number>;
}

export const EMPTY_CITATION_NUMBERING: CitationNumbering = Object.freeze({ sources: Object.freeze([]), numberById: new Map() });

export function numberMessageCitations(prose: string, outputs: AnalystOutputBlock[], citations: Citation[]): CitationNumbering {
  const citationById = new Map(citations.map((citation) => [citation.id, citation]));
  const numberByKey = new Map<string, number>();
  const numberById = new Map<string, number>();
  const sources: Citation[] = [];

  const assign = (id: string): void => {
    const citation = citationById.get(id);
    if (!citation || numberById.has(id)) {
      return;
    }
    const key = citationIdentityKey(citation);
    let number = numberByKey.get(key);
    if (number === undefined) {
      number = sources.push(citation);
      numberByKey.set(key, number);
    } else if (!sanitizeCitationRoute(sources[number - 1].route) && sanitizeCitationRoute(citation.route)) {
      sources[number - 1] = citation;
    }
    numberById.set(id, number);
  };

  proseCitationIds(prose).forEach(assign);
  outputs.flatMap(blockCitationIdGroups).forEach((ids) => ids.forEach(assign));
  return { sources, numberById };
}

export function resolveCitationMarkers(ids: string[] | null | undefined, numbering: CitationNumbering): CitationMarker[] {
  const markers: CitationMarker[] = [];
  const seenNumbers = new Set<number>();
  for (const id of ids ?? []) {
    const index = numbering.numberById.get(id);
    if (index === undefined || seenNumbers.has(index)) {
      continue;
    }
    seenNumbers.add(index);
    markers.push({ index, citation: numbering.sources[index - 1] });
  }
  return markers;
}

export function capCitationMarkers(markers: CitationMarker[]): CappedCitationMarkers {
  return {
    visible: markers.slice(0, MAX_VISIBLE_CITATION_MARKERS),
    hidden: markers.slice(MAX_VISIBLE_CITATION_MARKERS),
  };
}

export function markersForNumbers(numbers: readonly number[], numbering: CitationNumbering): CitationMarker[] {
  return numbers
    .filter((index) => index >= 1 && index <= numbering.sources.length)
    .map((index) => ({ index, citation: numbering.sources[index - 1] }));
}

export function readProseOverflowNumbers(text: string | null | undefined): number[] {
  return (text ?? '').split(/\s+/).filter(Boolean).map(Number).filter(Number.isInteger);
}

function citationIdentityKey(citation: Citation): string {
  return citation.recordId
    ? JSON.stringify([citation.entityType, citation.recordId])
    : JSON.stringify([citation.route, citation.label, null]);
}

function blockCitationIdGroups(block: AnalystOutputBlock): string[][] {
  if (block.type === AnalystOutputType.Table && block.table) {
    return block.table.rows.map((row) => row.citationIds ?? []);
  }
  if (block.type === AnalystOutputType.Chart && block.chart) {
    const heatmapGroup = block.chart.heatmap ? [block.chart.heatmap.citationIds ?? []] : [];
    return [...block.chart.series.map((series) => series.citationIds ?? []), ...heatmapGroup];
  }
  return [];
}

function proseCitationIds(content: string): string[] {
  return tokenCitationIds(stripReferenceLinkDefinitions(content).match(PROSE_CITATION_TOKEN_PATTERN) ?? []);
}

function tokenCitationIds(tokens: readonly string[]): string[] {
  return tokens
    .map((token: string) => PROSE_CITATION_MARKER_PATTERN.exec(token)?.[1])
    .filter((id): id is string => id !== undefined);
}

/** Matches every bracketed token the model can emit for a citation, valid or not: `[c1]`, but also
 * malformed forms like `[c1-c10, truncation note in tool result]` or `[c12–c20]`. Each match is then
 * checked against `PROSE_CITATION_MARKER_PATTERN` to see whether it's a real, single, resolvable id. */
const PROSE_CITATION_TOKEN_PATTERN = /\[c\d[^\]]*\]/g;
const PROSE_CITATION_MARKER_PATTERN = /^\[(c\d+)\]$/;
const PROSE_CITATION_RUN_PATTERN = new RegExp(String.raw`${PROSE_CITATION_TOKEN_PATTERN.source}(?:[^\S\r\n]*${PROSE_CITATION_TOKEN_PATTERN.source})*`, 'g');

export const PROSE_CITATION_OVERFLOW_SLOT_CLASS = 'citation-overflow-slot';

/** Matches a whole line that is a CommonMark reference-link definition — a bracketed label, a colon,
 * a destination, and an optional title, with nothing else on the line. Left in place, `marked` resolves
 * a citation chip's own rendered `[N]` text as a shortcut reference link whenever its label matches one
 * of these, splitting the chip's anchor into an empty element plus an unrelated sibling anchor pointing
 * at whatever the definition names. Stripped before markers are injected so the citation's own numeral
 * never collides with a label the model wrote elsewhere in the same answer. */
const REFERENCE_LINK_DEFINITION_LINE_PATTERN = /^ {0,3}\[[^\]\n]+\]: *(?:<[^<>\n]*>|\S+)(?: +(?:"[^"\n]*"|'[^'\n]*'|\([^()\n]*\)))? *$\n?/gm;

function stripReferenceLinkDefinitions(content: string): string {
  return content.replace(REFERENCE_LINK_DEFINITION_LINE_PATTERN, '');
}

/** Resolves `[cN]` markers left in an assistant answer's prose into linked superscript chips numbered
 * by the message's `CitationNumbering`, the same numbers its tables and charts show. Anything that isn't
 * a single resolvable id - a range, a stray note, an id with no matching citation - is dropped rather
 * than shown to the user, since the backend contract only ever guarantees single-id markers. */
export function resolveProseCitations(content: string, numbering: CitationNumbering): string {
  return stripReferenceLinkDefinitions(content).replace(PROSE_CITATION_RUN_PATTERN, (run) => {
    const ids = tokenCitationIds(run.match(PROSE_CITATION_TOKEN_PATTERN) ?? []);
    if (ids.filter((id) => numbering.numberById.has(id)).length <= MAX_VISIBLE_CITATION_MARKERS) {
      return run.replace(PROSE_CITATION_TOKEN_PATTERN, (token) => proseTokenHtml(token, numbering));
    }
    const { visible, hidden } = capCitationMarkers(resolveCitationMarkers(ids, numbering));
    const slot = hidden.length
      ? `<span class="${PROSE_CITATION_OVERFLOW_SLOT_CLASS}">${hidden.map(({ index }) => index).join(' ')}</span>`
      : '';
    return visible.map(proseMarkerHtml).join('') + slot;
  });
}

function proseTokenHtml(token: string, numbering: CitationNumbering): string {
  const [marker] = resolveCitationMarkers(tokenCitationIds([token]), numbering);
  return marker ? proseMarkerHtml(marker) : '';
}

function proseMarkerHtml({ index, citation }: CitationMarker): string {
  return `<sup class="citation-marker"><a class="citation-marker-link" data-citation-id="${escapeHtmlAttribute(citation.id)}" href="${escapeHtmlAttribute(sanitizeCitationRoute(citation.route))}">[${index}]</a></sup>`;
}

function escapeHtmlAttribute(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/** A citation route may only be an app-relative navigation path: a leading slash, no control
 * character, and it must resolve to the app's own origin, so `//host`, `/\host` and `/\t/host`
 * are rejected along with every absolute URL.
 *
 * Absolute `http(s)` URLs are deliberately NOT allowed. Angular's sanitizer already strips
 * `javascript:` and `data:` from an href, so those were never the reachable case — an external
 * `https://` URL is, because the citation anchors carry both `[href]` and `(click)`, and a
 * modifier-key click (Ctrl, middle) bypasses the click handler and lets the browser navigate to
 * the raw href. Allowing absolute URLs would leave an open redirect driven by a server-supplied
 * value. Citation routes are composed server-side as in-app paths, so nothing legitimate is lost.
 *
 * Applied once at the wire boundary (`ChatHttpService.mapMessage`) so every consumer — the prose
 * markers below, the table/chart `[href]` bindings and the sources list — is covered from one
 * place. Re-applied here as defence-in-depth for callers that build markup directly. */
export function sanitizeCitationRoute(route: string): string {
  const isAppRelative = !!route?.startsWith('/') && !/\p{Cc}/u.test(route) && resolvesToAppOrigin(route);
  return isAppRelative ? route : '';
}

function resolvesToAppOrigin(route: string): boolean {
  try {
    return new URL(route, ROUTE_RESOLUTION_BASE).origin === ROUTE_RESOLUTION_BASE;
  } catch {
    return false;
  }
}
